import {combineLatest, Observable, of, of as observableOf} from 'rxjs';

import {first, map, mergeMap, shareReplay, take, takeWhile} from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  EnvironmentConfigService,
  EnvironmentAttributes,
} from '../../core/services/environment-config.service';
import {LanguageService} from '../../language/language.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DatatableMultivalueDialogComponent} from '../component';
import {Router} from '@angular/router';
import {CacheService} from '../../cache/cache.service';
import {ResourceService} from '../../resource/resource.service';
import {LocalDatePipe} from '../pipes/local-date.pipe';
import {CtrlFileDownloadComponent} from '../../resource/detail/controls/ctrl-filedownload.component';
import {DomSanitizer} from '@angular/platform-browser';
import {DomUtilsService} from '../../utils/dom-utils.service';
import {LayoutService} from '../../layout/layout.service';
import {Endpoint} from '../../layout/endpoint';

@Component({
  selector: 'request-summary-control',
  templateUrl: './request-summary-control.component.html',
  styleUrls: ['./request-summary-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequestSummaryControlComponent implements OnInit {
  constructor(
    private cacheService: CacheService,
    private resourceService: ResourceService,
    private router: Router,
    private dialog: MatDialog,
    private localDatePipe: LocalDatePipe,
    private sanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef,
    private domUtils: DomUtilsService,
    private layoutService: LayoutService,
    private envConfigService: EnvironmentConfigService,
  ) {}
  isCloud: boolean = false;

  private _request: any = null;
  @Input() displayNameCallback: Function = null;
  @Input() objectTypeCallback: Function = null;
  @Input() targetsToFilter: string[] = null;
  @Output() openDetail: EventEmitter<any> = new EventEmitter();

  showAllRows: boolean = false;

  private observableCache: Observable<any>[] = [];

  parsedParameters: any = null;
  pageSize = 100;
  maxRows: number = this.pageSize;

  noItems: boolean = false;
  targetsNotDistinct: boolean = false;

  ngOnInit(): void {
    this.noItems = false;
  }

  @Input() set request(request: any) {
    this._request = request;
    if (request == null) return;
    this.layoutService.endpoint$.pipe(take(1)).subscribe((env: Endpoint) => {
      if (!env.isCloud()) {
        // Path 1: On-prem — XML in RequestParameter attribute
        this.isCloud = false;
        this.parsedParameters = this.parseRequestParametersOnPrem(request, 'RequestParameter');
      } else {
        const contentAttrName = this.envConfigService.getAttributeName('Content');
        const content = contentAttrName ? request[contentAttrName] : null;
        if (this.isCloudNativeRequest(content)) {
          // Path 3: Cloud-native — JSON in content/before
          this.isCloud = true;
          this.parsedParameters = this.computeCloudChanges(request);
        } else {
          // Path 2: Cloud with on-prem-style — XML in content
          this.isCloud = false;
          this.parsedParameters = this.parseRequestParametersOnPrem(request, 'Content');
        }
      }
      this.changeDetector.markForCheck();
    });
  }

  private isCloudNativeRequest(content: any): boolean {
    return content != null && typeof content === 'object' && !Array.isArray(content);
  }

  private parseRequestParametersOnPrem(
    request: any,
    attributeKey: keyof EnvironmentAttributes,
  ): any[] {
    if (request == null) return null;
    const requestParamAttrName = this.envConfigService.getAttributeName(attributeKey);
    const content = requestParamAttrName ? request[requestParamAttrName] : null;
    if (content == null) return [];

    // Normalize to array: content can be a single XML string or an array of XML strings
    const params: string[] = Array.isArray(content) ? content : [content];

    if (params.length === 0) return [];
    let parsed = [];
    const xmlReqParamRegex: RegExp =
      /^\s*<RequestParameter[^>]+>\s*<Target>([^<]*)<\/Target>\s*<Calculated>([^<]*)<\/Calculated>\s*<PropertyName>([^<]*)<\/PropertyName>\s*(?:<Value[^>]+xsi:type=\\?"([^"\\]*)\\?"[^>]*>([^<]*)<\/Value>)?\s*<Operation>([^<]*)<\/Operation>\s*(?:<Mode>([^<]*)<\/Mode>\s*)?<\/RequestParameter>$/;

    params.forEach((p: string) => {
      const matches = xmlReqParamRegex.exec(p);
      if (matches != null) {
        let type = matches[4];
        try {
          type = type.split(':')[1];
          type = type.charAt(0).toLocaleUpperCase() + type.substr(1);
        } catch (e) {}
        let targetGuid = matches[1];

        const propertyDisplayAttribute = this.getTargetObjectType(targetGuid).pipe(
          mergeMap(targetObjectType =>
            this.cacheService.getDisplayAttribute(matches[3], targetObjectType),
          ),
          shareReplay(1),
        );

        let download = undefined;

        let value: any = matches[5];
        let rawValue: any = matches[5];
        let valueObs: Observable<string> = null;
        if (type === 'Guid') {
          valueObs = this.getDisplayNameForOnPrem(value);
          value = observableOf(value);
        } else if (type === 'String') {
          value = this.domUtils.decodeHtmlEntities(value);
          value = observableOf(value);
        } else if (type === 'Base64Binary' && value !== null) {
          value = propertyDisplayAttribute.pipe(
            mergeMap(da => {
              if (da == null || da.FileName == null) {
                return this.cacheService
                  .getLanguageResource()
                  .pipe(
                    map(lr =>
                      this.domUtils.decodeHtmlEntities(
                        LanguageService.translate(
                          'RequestApprovalBinary',
                          'Binary (not shown)',
                          lr,
                        ),
                      ),
                    ),
                  );
              } else {
                return observableOf(value);
              }
            }),
          );
          download = propertyDisplayAttribute.pipe(
            map(da => {
              if (da != null && da.FileName != null) {
                const blob: Blob = CtrlFileDownloadComponent.b64ToBlob(rawValue);
                const linkText = this.cacheService
                  .getLanguageResource()
                  .pipe(
                    map(lr =>
                      LanguageService.translate(
                        'RequestApprovalDownloadLinkText',
                        'Download {0}',
                        lr,
                        [da.FileName],
                      ),
                    ),
                  );

                return {
                  blob: blob,
                  objectUrl:
                    blob != null
                      ? this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob))
                      : null,
                  fileName: da.FileName,
                  linkText: linkText,
                };
              } else {
                return null;
              }
            }),
          );
        } else if (type === 'Base64Binary' && value === null) {
          value = this.cacheService
            .getLanguageResource()
            .pipe(
              map(lr =>
                this.domUtils.decodeHtmlEntities(
                  LanguageService.translate('RequestApprovalBinaryEmpty', 'Empty', lr),
                ),
              ),
            );
        } else if (type === 'DateTime') {
          value = this.localDatePipe.transform(value);
        } else {
          value = observableOf(value);
        }

        parsed.push({
          Target: targetGuid,
          Calculated: matches[2].toLowerCase().trim() === 'true',
          PropertyName: matches[3],
          PropertyDisplayName: propertyDisplayAttribute.pipe(
            map(da => (da != null ? da.DisplayName : matches[3])),
          ),
          ValueType: type,
          Value: value,
          RawValue: rawValue,
          DisplayName: valueObs,
          Operation: matches[6],
          Mode: matches[7],
          Download: download,
        });
      }
    });

    this.targetsNotDistinct = new Set(parsed.map(x => x.Target)).size > 1;
    if (this.targetsNotDistinct) {
      parsed.forEach(obj => {
        obj.TargetDisplayName = this.getDisplayNameForOnPrem(obj.Target);
      });
    }

    this.noItems = parsed.length === 0;
    return parsed;
  }

  private getDisplayNameForOnPrem(guid: string): Observable<string> {
    let valueObs: Observable<string> = null;
    if (this.displayNameCallback !== null && typeof this.displayNameCallback === 'function') {
      valueObs = this.displayNameCallback(guid);
    } else {
      valueObs = this.observableCache[guid];
      if (valueObs == null) {
        valueObs = this.resourceService.getBasicResource(guid).pipe(
          mergeMap(r => {
            if (r == null) {
              return combineLatest([
                this.cacheService.getLanguageResource().pipe(first()),
                this.cacheService
                  .getDisplayAttribute(
                    this.envConfigService.getAttributeName('DisplayName'),
                    this.envConfigService.getObjectTypeName('Resource'),
                  )
                  .pipe(first()),
              ]).pipe(
                map(
                  ([lr, da]) =>
                    '⟨ ' +
                    LanguageService.translate('DatatableNo', 'No', lr) +
                    ' ' +
                    (da != null ? da.Caption : 'Display Name') +
                    ' ⟩',
                ),
              );
            }
            const displayNameAttrName = this.envConfigService.getAttributeName('DisplayName');
            return observableOf(displayNameAttrName ? r[displayNameAttrName] : guid);
          }),
          shareReplay(1),
        );
        this.observableCache[guid] = valueObs;
      }
    }
    return valueObs;
  }

  visibleOnPremParameters(): any[] {
    if (!this.showAllRows && this.targetsToFilter !== null && this.parsedParameters != null) {
      return this.parsedParameters.filter(obj => this.targetsToFilter.includes(obj.Target));
    } else {
      return this.parsedParameters;
    }
  }

  visibleOnPremParametersSlice(): any[] {
    return this.visibleOnPremParameters()?.slice(0, this.maxRows);
  }

  onPremHasMoreRows(): boolean {
    return this.visibleOnPremParameters()?.length > this.maxRows;
  }

  computeCloudChanges(request: any): Observable<any[]> {
    if (request == null) return null;
    const beforeAttrName = this.envConfigService.getAttributeName('Before');
    const contentAttrName = this.envConfigService.getAttributeName('Content');
    const targetObjectTypeAttrName = this.envConfigService.getAttributeName('TargetObjectType');

    let before = beforeAttrName ? request[beforeAttrName] : null;
    let content = contentAttrName ? request[contentAttrName] : null;
    let objType = targetObjectTypeAttrName ? request[targetObjectTypeAttrName] : null;

    const guidMap = guid => {
      let obs = this.observableCache[guid];
      if (obs == null) {
        obs = this.resourceService.getBasicResource(guid).pipe(
          map(r => {
            if (r == null) {
              return {id: null, dn: ''};
            } else {
              const objectIdAttrName = this.envConfigService.getAttributeName('ObjectID');
              const displayNameAttrName = this.envConfigService.getAttributeName('DisplayName');
              return {
                id: objectIdAttrName ? r[objectIdAttrName] : null,
                dn: displayNameAttrName ? r[displayNameAttrName] : '',
              };
            }
          }),
          shareReplay(1),
        );
        this.observableCache[guid] = obs;
      }
      return obs;
    };

    const previewCount = 4;

    let parsed: Observable<any>[] = [];
    const operationAttrName = this.envConfigService.getAttributeName('Operation');
    const operation: 'Create' | 'Modify' | 'Delete' = operationAttrName
      ? request[operationAttrName]
      : null;
    for (let key in content) {
      if (!content.hasOwnProperty(key)) continue;

      parsed.push(
        this.cacheService.getDisplayAttribute(key, objType).pipe(
          map(da => {
            let value = content == null ? null : content[key];
            let valBefore = before == null ? null : before[key];
            let multivalued = false;
            let type = 'String';
            if (da) {
              type = da.DataType;
              multivalued = da.Multivalued;
            }

            if (multivalued) {
              if (operation === 'Modify') {
                let add = [];
                let remove = [];
                if (valBefore == null || valBefore.length === 0) {
                  add = value;
                  remove = [];
                } else if (value == null || value.length === 0) {
                  add = [];
                  remove = valBefore;
                } else {
                  add = value.filter(x => valBefore.indexOf(x) === -1);
                  remove = valBefore.filter(x => value.indexOf(x) === -1);
                }

                if (type === 'Reference') {
                  add = add.map(guidMap);
                  remove = remove.map(guidMap);
                }

                let addPreview = add.slice(0, previewCount);
                let removePreview = remove.slice(0, previewCount);

                let retList = [];

                if (add != null && add.length > 0) {
                  retList.push({
                    PropertyName: key,
                    DataType: type,
                    Multivalued: true,
                    Value: add,
                    Preview: addPreview,
                    RemainingValueCount: Math.max(0, add.length - previewCount),
                    Operation: 'Add',
                  });
                }
                if (remove != null && remove.length > 0) {
                  retList.push({
                    PropertyName: key,
                    DataType: type,
                    Multivalued: true,
                    Value: remove,
                    Preview: removePreview,
                    RemainingValueCount: Math.max(0, remove.length - previewCount),
                    Operation: 'Remove',
                  });
                }
                return retList;
              } else {
                if (type === 'Reference' && value != null && Array.isArray(value)) {
                  value = value.map(guidMap);
                }
                let preview = value.slice(0, previewCount);

                return [
                  {
                    PropertyName: key,
                    DataType: type,
                    Multivalued: true,
                    Value: value,
                    Preview: preview,
                    RemainingValueCount: Math.max(0, value.length - previewCount),
                    Operation: operation,
                  },
                ];
              }
            } else {
              // Single Value
              let dnObs = null;
              let dnObsBefore = null;
              if (type === 'Reference') {
                dnObs = guidMap(value).pipe(map(x => x.dn));
                dnObsBefore = guidMap(valBefore).pipe(map(x => x.dn));
              } else if (type === 'String') {
                value = this.domUtils.decodeHtmlEntities(value);
                value = observableOf(value);
                valBefore = this.domUtils.decodeHtmlEntities(valBefore);
                valBefore = observableOf(valBefore);
              } else if (type === 'Binary') {
                if (value === null) {
                  value = this.cacheService
                    .getLanguageResource()
                    .pipe(
                      map(lr =>
                        this.domUtils.decodeHtmlEntities(
                          LanguageService.translate('RequestApprovalBinaryEmpty', 'Empty', lr),
                        ),
                      ),
                    );
                } else {
                  value = this.cacheService
                    .getLanguageResource()
                    .pipe(
                      map(lr =>
                        this.domUtils.decodeHtmlEntities(
                          LanguageService.translate(
                            'RequestApprovalBinary',
                            'Binary (not shown)',
                            lr,
                          ),
                        ),
                      ),
                    );
                }
                if (valBefore === null) {
                  valBefore = this.cacheService
                    .getLanguageResource()
                    .pipe(
                      map(lr =>
                        this.domUtils.decodeHtmlEntities(
                          LanguageService.translate('RequestApprovalBinaryEmpty', 'Empty', lr),
                        ),
                      ),
                    );
                } else {
                  valBefore = this.cacheService
                    .getLanguageResource()
                    .pipe(
                      map(lr =>
                        this.domUtils.decodeHtmlEntities(
                          LanguageService.translate(
                            'RequestApprovalBinary',
                            'Binary (not shown)',
                            lr,
                          ),
                        ),
                      ),
                    );
                }
              } else if (type === 'DateTime') {
                value = this.localDatePipe.transform(value);
                valBefore = this.localDatePipe.transform(valBefore);
              } else {
                value = observableOf(value);
                valBefore = observableOf(valBefore);
              }

              return [
                {
                  PropertyName: key,
                  DataType: type,
                  Multivalued: multivalued,
                  Value: value,
                  ValueBefore: valBefore,
                  DisplayName: dnObs,
                  DisplayNameBefore: dnObsBefore,
                  Operation: operation,
                },
              ];
            }
          }),
        ),
      );
    }

    this.targetsNotDistinct = false;
    this.noItems = parsed.length === 0;

    return combineLatest(parsed).pipe(
      map(changeLists => {
        const changes = [];
        changeLists.forEach(changeList => {
          changes.push(...changeList);
        });
        return changes;
      }),
    );
  }

  private getTargetObjectType(targetGuid: string): Observable<string> {
    if (this.objectTypeCallback !== null && typeof this.objectTypeCallback === 'function') {
      return this.objectTypeCallback(targetGuid);
    } else {
      return this.resourceService.getBasicResource(targetGuid).pipe(
        map(x => {
          if (x) {
            const objectTypeAttrName = this.envConfigService.getAttributeName('ObjectType');
            return objectTypeAttrName ? x[objectTypeAttrName] : null;
          } else {
            return null;
          }
        }),
      );
    }
  }

  onChipClick(obj: Observable<any>, event: MouseEvent): void {
    obj.pipe(first()).subscribe((o: any) => {
      if (o != null && o.id != null) {
        if (event.ctrlKey || event.button === 1) {
          window.open('#/get/' + o.id);
        } else {
          this.router.navigate(['/get', o.id]);
        }
      }
    });
  }

  onRemainingClick(
    value: any,
    attribute: string,
    dataType: string,
    displayName: string = undefined,
  ) {
    const dialogRef: MatDialogRef<DatatableMultivalueDialogComponent> = this.dialog.open(
      DatatableMultivalueDialogComponent,
    );
    if (displayName == undefined) {
      const displayNameAttrName = this.envConfigService.getAttributeName('DisplayName');
      displayName = displayNameAttrName ? this._request[displayNameAttrName] : '';
    }
    dialogRef.componentInstance.displayName = displayName;
    dialogRef.componentInstance.attributeName = attribute;
    dialogRef.componentInstance.model = value;
    dialogRef.componentInstance.dataType = dataType;
    let open: boolean = true;
    dialogRef.componentInstance.openDetailEmitter
      .pipe(takeWhile(() => open))
      // .catch((error, caught) => this.handleError(error, caught))
      .subscribe((val: {objectID: string; event: MouseEvent}) => {
        if (val.event.ctrlKey || val.event.button === 1) {
          window.open('#/get/' + val.objectID);
        } else {
          this.router.navigate(['/get', val.objectID]);
          dialogRef.close(null);
          open = false;
        }
      });
    dialogRef
      .afterClosed()
      .pipe(first()) //.catch((error, caught) => this.handleError(error, caught))
      .subscribe(x => (open = false));
  }

  get showBeforeValue(): boolean {
    if (this._request == null) return false;
    const operationAttrName = this.envConfigService.getAttributeName('Operation');
    return operationAttrName ? this._request[operationAttrName] !== 'Create' : false;
  }

  get showValue(): boolean {
    if (this._request == null) return false;
    const operationAttrName = this.envConfigService.getAttributeName('Operation');
    return operationAttrName ? this._request[operationAttrName] !== 'Delete' : false;
  }

  get numberOfColumns(): number {
    return this.targetsNotDistinct ? 5 : 4;
  }
}
